import { useNavigate } from 'react-router-dom';
import { Modal } from './Modal';
import { useConfirm } from './ConfirmProvider';
import type { Site } from '../lib/db';

interface Props {
  open: boolean;
  onClose: () => void;
  sites: Site[];
  currentSiteId: string | null;
  onPick: (siteId: string) => void;
}

/**
 * The "switch active site" UI. Opens from the ActiveSiteBanner's "Switch site"
 * button. Shows every site in the org as a tappable row; the currently-active
 * one is marked and disabled (you can't switch to the site you're already on).
 *
 * Switching is a destination-aware action — the next form, sign-in, RAMS,
 * accident report, etc. gets filed against the newly-chosen site, NOT the old
 * one. The Brutal Review flagged this as the single most common cause of
 * "users misfile data in their first week and blame us" complaints, so we add
 * an explicit confirmation step. Two taps to switch (button → site row), then
 * a confirm modal that calls out the consequence in plain English. This trades
 * one extra tap for far fewer accidental misfilings.
 */
export function SiteSwitcherSheet({ open, onClose, sites, currentSiteId, onPick }: Props) {
  const { confirm } = useConfirm();
  const nav = useNavigate();

  if (!open) return null;

  const currentSite = sites.find((s) => s.id === currentSiteId) ?? null;

  async function handlePick(site: Site) {
    if (site.id === currentSiteId) return; // already active
    const ok = await confirm({
      title: `Switch active site?`,
      body: (
        <>
          <p style={{ margin: '0 0 10px' }}>
            Anything you record next — sign-ins, forms, RAMS, photos, accident
            reports — will be filed against{' '}
            <strong>{site.name}</strong>
            {currentSite ? <> and <em>not</em> {currentSite.name}.</> : '.'}
          </p>
          <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 14 }}>
            You can switch back any time, but already-filed records stay with the
            site they were saved to.
          </p>
        </>
      ),
      confirmLabel: `Switch to ${site.name}`,
      cancelLabel: 'Stay on current site',
    });
    if (!ok) return;
    onPick(site.id);
    onClose();
  }

  function handleAddNew() {
    onClose();
    nav('/sites?add=1');
  }

  return (
    <Modal open={open} title="Switch active site" onClose={onClose}>
      {/* Promote "+ Add a new site" to the top as a primary action — it was
          previously a ghost button at the bottom of the modal, which made
          adding a site feel hidden. Now it's the first thing you see. */}
      <button
        type="button"
        className="btn btn-primary site-switcher-add-cta"
        onClick={handleAddNew}
      >
        + Add a new site
      </button>

      {sites.length === 0 ? (
        <p style={{ margin: '0 0 16px', color: 'var(--ink-soft)' }}>
          You don't have any sites yet. Tap the button above to add your first.
        </p>
      ) : (
        <ul className="site-switcher-list" role="list">
          {sites.map((s) => {
            const isCurrent = s.id === currentSiteId;
            const subtitle = projectTypeLabel(s.project_type) || s.address || null;
            return (
              <li key={s.id}>
                <button
                  type="button"
                  className={`site-switcher-row${isCurrent ? ' is-current' : ''}`}
                  onClick={() => handlePick(s)}
                  disabled={isCurrent}
                  aria-current={isCurrent ? 'true' : undefined}
                >
                  <div className="site-switcher-row-text">
                    <div className="site-switcher-row-name">{s.name}</div>
                    {subtitle && (
                      <div className="site-switcher-row-sub">{subtitle}</div>
                    )}
                  </div>
                  {isCurrent ? (
                    <span className="site-switcher-current-badge" aria-hidden="true">
                      ✓ Currently active
                    </span>
                  ) : (
                    <span className="site-switcher-pick">Switch →</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="btn-stack" style={{ marginTop: 12 }}>
        <button type="button" className="btn btn-ghost" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}

function projectTypeLabel(t: Site['project_type']): string | null {
  if (t === 'quick_job') return 'Quick job';
  if (t === 'small_refurb') return 'Small refurb';
  if (t === 'full_site') return 'Full site';
  return null;
}
